import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TMemberCapacitySummary, TProjectCapacityRisk } from "@/types/project-capacity";
import type {
  TWorkspaceCapacityGroupBy,
  TWorkspaceCapacityMember,
  TWorkspaceCapacitySummary,
} from "@/types/workspace-capacity";
import { CAPACITY_MEMBER_PAGE_SIZE } from "@/lib/capacity-pagination";

const EMPTY_ISSUE_IDS: Set<string> = new Set();

export class ProjectCapacityStore {
  capacityByProjectId = observable.map<string, TMemberCapacitySummary>();
  loadingByProjectId = observable.map<string, boolean>();
  errorByProjectId = observable.map<string, string | null>();
  allocationByKey = observable.map<string, TWorkspaceCapacitySummary>();
  loadingAllocationByKey = observable.map<string, boolean>();
  loadingMoreAllocationByKey = observable.map<string, boolean>();
  errorAllocationByKey = observable.map<string, string | null>();
  risksByKey = observable.map<string, TProjectCapacityRisk[]>();
  loadingRisksByKey = observable.map<string, boolean>();
  // Work-item ids with an allocation issue (#2352), keyed by project id. The
  // allocation-warning banner (mounted on every work view) is the single writer
  // — it fetches project capacity, derives the set, and publishes it here so the
  // Table's per-row name cell can flag the same tasks without recomputing.
  allocationIssueIdsByProject = observable.map<string, Set<string>>();

  private rootStore: RootStore;
  /** In-flight risk reads, so a second caller awaits the first rather than
   * being told "already loading" and reading an empty cache. Not observable. */
  private inflightRisks = new Map<string, Promise<void>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false, inflightRisks: false } as never, {
      autoBind: true,
    });
  }

  getSummary(projectId: string): TMemberCapacitySummary | undefined {
    return this.capacityByProjectId.get(projectId);
  }

  /** Work items in this project flagged with an allocation issue (#2352).
   *  Empty until the allocation-warning banner has derived it. */
  getAllocationIssueIds(projectId: string): Set<string> {
    return this.allocationIssueIdsByProject.get(projectId) ?? EMPTY_ISSUE_IDS;
  }

  hasAllocationIssue(projectId: string, issueId: string): boolean {
    return this.allocationIssueIdsByProject.get(projectId)?.has(issueId) ?? false;
  }

  setAllocationIssueIds(projectId: string, ids: Set<string>): void {
    runInAction(() => {
      this.allocationIssueIdsByProject.set(projectId, ids);
    });
  }

  getProjectCapacity(
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
  ): TWorkspaceCapacitySummary | undefined {
    return this.allocationByKey.get(this.allocationKey(projectId, startDate, endDate, groupBy));
  }

  isLoadingProjectCapacity(
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
  ): boolean {
    return Boolean(
      this.loadingAllocationByKey.get(this.allocationKey(projectId, startDate, endDate, groupBy)),
    );
  }

  isLoadingMoreProjectCapacity(
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
  ): boolean {
    return Boolean(
      this.loadingMoreAllocationByKey.get(
        this.allocationKey(projectId, startDate, endDate, groupBy),
      ),
    );
  }

  getProjectRisks(
    projectId: string,
    startDate: string,
    endDate: string,
    memberIds?: readonly string[],
  ): TProjectCapacityRisk[] | undefined {
    return this.risksByKey.get(this.riskKey(projectId, startDate, endDate, memberIds));
  }

  isLoadingProjectRisks(
    projectId: string,
    startDate: string,
    endDate: string,
    memberIds?: readonly string[],
  ): boolean {
    return Boolean(
      this.loadingRisksByKey.get(this.riskKey(projectId, startDate, endDate, memberIds)),
    );
  }

  getProjectCapacityError(
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
  ): string | null | undefined {
    return this.errorAllocationByKey.get(
      this.allocationKey(projectId, startDate, endDate, groupBy),
    );
  }

  async fetchCapacitySummary(
    workspaceSlug: string,
    projectId: string,
  ): Promise<TMemberCapacitySummary> {
    runInAction(() => {
      this.loadingByProjectId.set(projectId, true);
      this.errorByProjectId.set(projectId, null);
    });
    try {
      const summary = await this.rootStore.projectCapacityService.getMemberCapacitySummary(
        workspaceSlug,
        projectId,
      );
      runInAction(() => {
        this.capacityByProjectId.set(projectId, summary);
      });
      return summary;
    } catch (err) {
      runInAction(() => {
        this.errorByProjectId.set(projectId, err instanceof Error ? err.message : "error");
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loadingByProjectId.set(projectId, false);
      });
    }
  }

  async fetchProjectCapacity(
    workspaceSlug: string,
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: { memberCursor?: string | null } = {},
  ): Promise<TWorkspaceCapacitySummary> {
    const key = this.allocationKey(projectId, startDate, endDate, groupBy);
    runInAction(() => {
      this.loadingAllocationByKey.set(key, true);
      this.errorAllocationByKey.set(key, null);
    });
    try {
      const [totals, page] = await Promise.all([
        this.rootStore.projectCapacityService.getProjectCapacityTotals(
          workspaceSlug,
          projectId,
          startDate,
          endDate,
          groupBy,
        ),
        this.rootStore.projectCapacityService.getProjectCapacity(
          workspaceSlug,
          projectId,
          startDate,
          endDate,
          groupBy,
          {
            includeTotals: false,
            memberPageSize: CAPACITY_MEMBER_PAGE_SIZE,
            memberCursor: options.memberCursor,
          },
        ),
      ]);
      const summary: TWorkspaceCapacitySummary = {
        ...page,
        days: totals.days,
      };
      runInAction(() => {
        this.allocationByKey.set(key, summary);
      });
      return summary;
    } catch (err) {
      runInAction(() => {
        this.errorAllocationByKey.set(key, err instanceof Error ? err.message : "error");
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loadingAllocationByKey.set(key, false);
      });
    }
  }

  async fetchNextProjectCapacityPage(
    workspaceSlug: string,
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
  ): Promise<TWorkspaceCapacitySummary | undefined> {
    const key = this.allocationKey(projectId, startDate, endDate, groupBy);
    const current = this.allocationByKey.get(key);
    const pagination = current?.members_pagination;
    if (!current || !pagination?.has_next || this.loadingMoreAllocationByKey.get(key))
      return current;

    runInAction(() => {
      this.loadingMoreAllocationByKey.set(key, true);
    });
    try {
      const nextPage = await this.rootStore.projectCapacityService.getProjectCapacity(
        workspaceSlug,
        projectId,
        startDate,
        endDate,
        groupBy,
        {
          includeTotals: false,
          memberPageSize: pagination.page_size,
          memberCursor: pagination.next_cursor,
        },
      );
      let merged: TWorkspaceCapacitySummary | undefined;
      runInAction(() => {
        const latest = this.allocationByKey.get(key);
        if (!latest) return;
        merged = {
          ...latest,
          members: mergeCapacityMembers(latest.members, nextPage.members),
          members_pagination: nextPage.members_pagination,
        };
        this.allocationByKey.set(key, merged);
      });
      return merged;
    } finally {
      runInAction(() => {
        this.loadingMoreAllocationByKey.set(key, false);
      });
    }
  }

  async fetchProjectCapacityRisks(
    workspaceSlug: string,
    projectId: string,
    startDate: string,
    endDate: string,
    memberIds?: readonly string[],
  ): Promise<void> {
    const key = this.riskKey(projectId, startDate, endDate, memberIds);
    // Skip refetch when this project+window is already cached. The timeline
    // effect re-runs on every `rows` change (e.g. toggling a member row), so
    // without this guard a stable window re-fires one request per already-loaded
    // project. `invalidate` clears the cache when data changes.
    if (this.risksByKey.has(key)) return;
    // A concurrent caller awaits the same request. Returning early here instead
    // would resolve its `Promise.all` before the cache is written, so it would
    // read nothing and render an empty strip.
    const inflight = this.inflightRisks.get(key);
    if (inflight) return inflight;
    const request = this.loadProjectCapacityRisks(
      key,
      workspaceSlug,
      projectId,
      startDate,
      endDate,
      memberIds,
    );
    this.inflightRisks.set(key, request);
    return request;
  }

  private async loadProjectCapacityRisks(
    key: string,
    workspaceSlug: string,
    projectId: string,
    startDate: string,
    endDate: string,
    memberIds?: readonly string[],
  ): Promise<void> {
    runInAction(() => {
      this.loadingRisksByKey.set(key, true);
    });
    try {
      const response = await this.rootStore.projectCapacityService.getProjectCapacityRisks(
        workspaceSlug,
        projectId,
        startDate,
        endDate,
        memberIds,
      );
      runInAction(() => {
        this.risksByKey.set(key, response.risks);
      });
    } catch (error) {
      console.warn("capacity risks failed", {
        projectId,
        startIso: startDate,
        endIso: endDate,
        error,
      });
    } finally {
      this.inflightRisks.delete(key);
      runInAction(() => {
        this.loadingRisksByKey.set(key, false);
      });
    }
  }

  invalidate(projectId: string): void {
    this.capacityByProjectId.delete(projectId);
    this.allocationIssueIdsByProject.delete(projectId);
    for (const key of this.allocationByKey.keys()) {
      if (key.startsWith(`${projectId}|`)) {
        this.allocationByKey.delete(key);
        this.loadingAllocationByKey.delete(key);
        this.loadingMoreAllocationByKey.delete(key);
        this.errorAllocationByKey.delete(key);
      }
    }
    for (const key of this.risksByKey.keys()) {
      if (key.includes(`|${projectId}|`)) {
        this.risksByKey.delete(key);
        this.loadingRisksByKey.delete(key);
      }
    }
  }

  /**
   * Risks are cached per project, window AND member scope.
   *
   * The scope is part of the answer, not just the request: it moves both sides of a
   * capacity gap, so the same project over the same window says different things to a
   * team grid and to the workspace one. Keyed without it, whichever asked first would
   * answer for the other. Sorted, because the caller's order is the order its rows
   * happen to be in.
   *
   * `invalidate` matches on `|<projectId>|`, so the extra segment stays behind it.
   */
  private riskKey(
    projectId: string,
    startDate: string,
    endDate: string,
    memberIds?: readonly string[],
  ): string {
    const scope = memberIds?.length ? [...memberIds].sort().join(",") : "all";
    return `risk|${projectId}|${startDate}|${endDate}|${scope}`;
  }

  private allocationKey(
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy,
  ): string {
    return `${projectId}|${startDate}|${endDate}|${groupBy}`;
  }
}

function mergeCapacityMembers(
  current: TWorkspaceCapacityMember[],
  next: TWorkspaceCapacityMember[],
): TWorkspaceCapacityMember[] {
  const byId = new Map(current.map((member) => [member.id, member]));
  for (const member of next) byId.set(member.id, member);
  return Array.from(byId.values());
}
